import { env } from "@/constants/env";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import {
  breadcrumbNoTokenAvailable,
  breadcrumbStaleTokenServed,
  breadcrumbToken401Recovered,
  getAuthOperationVersion,
  reportApi401SignOut,
} from "@/utils/auth/logout-telemetry";
import { waitForClerkReady } from "@/utils/auth/wait-for-clerk-ready";
import { decodeJWT } from "@/utils/jwt";
import type { BrowserClerk } from "@clerk/clerk-react";
import axios, { AxiosError } from "axios";
import { ApiRequestOptions } from "./core/ApiRequestOptions";
import { OpenAPI } from "./core/OpenAPI";

const clerkWindow = window as Window & { Clerk?: BrowserClerk };

type Resolver<T> = (options: ApiRequestOptions) => Promise<T>;
const FORCE_TOKEN_REFRESH_HEADER = "x-force-token-refresh";
const TOKEN_REFRESH_WINDOW_MS = 60 * 1000;
// Above AgentCore's 60s server-side rejection threshold. The extra 60s
// margin accommodates network latency and pauses between token mint and
// request arrival (e.g. transcription, slow uploads, long-running agent
// turns where the cached token can age past AgentCore's floor before send).
const AGENT_TOKEN_PROACTIVE_REFRESH_MS = 120 * 1000;
// Only the legacy direct runtime invocation uses the AgentCore-template JWT.
// Durable turn status/cancel endpoints live under the same /agent namespace
// but are served by the main API and require the normal session JWT.
const AGENT_RUNTIME_URL_PREFIX = "/api/v1/agent/invocations";
const AGENT_JWT_TEMPLATE = "nutricoach-agent";

export const isAgentTokenNearExpiry = (token: string): boolean => {
  const decoded = decodeJWT(token);
  if (typeof decoded?.exp !== "number") return true;
  return decoded.exp * 1000 - Date.now() < AGENT_TOKEN_PROACTIVE_REFRESH_MS;
};

const checkTokenExpiration = (token: string): boolean => {
  const decodedToken = decodeJWT(token);
  const expiresAt = decodedToken?.exp ? new Date(decodedToken.exp * 1000) : null;
  if (expiresAt) {
    const now = new Date();
    const timeDifference = expiresAt.getTime() - now.getTime();
    if (timeDifference < TOKEN_REFRESH_WINDOW_MS) {
      return true;
    }
    return false;
  }
  return true;
};

const getTokenFromClerkSession = async (forceRefresh: boolean): Promise<string> => {
  const operationVersion = getAuthOperationVersion();
  // Cold start / resume: the Clerk singleton may exist while its session is still
  // rehydrating. Wait for it to finish booting before concluding there is no
  // session — otherwise we'd serve a stale cached token and trigger a false 401
  // sign-out. Instant when Clerk is already loaded (the common case).
  if (!clerkWindow.Clerk?.session) {
    const readiness = await waitForClerkReady();
    if (readiness !== "ready") {
      throw new Error("Unable to restore your session. Please try again.");
    }
  }
  if (clerkWindow.Clerk && clerkWindow.Clerk.session) {
    const session = clerkWindow.Clerk.session;
    const sessionToken = await session.getToken({ skipCache: forceRefresh });
    if (getAuthOperationVersion() !== operationVersion || clerkWindow.Clerk?.session !== session) {
      throw new Error("Session changed while refreshing authentication.");
    }
    return sessionToken ?? "";
  }
  return "";
};

// Force a freshly-minted default session token and persist it. Reused by the
// getToken force path and the 401 refresh-and-retry below.
let pendingRefresh: {
  session: unknown;
  version: number;
  promise: Promise<string>;
} | null = null;

const refreshSessionToken = (): Promise<string> => {
  const session = clerkWindow.Clerk?.session;
  const version = getAuthOperationVersion();
  if (pendingRefresh && pendingRefresh.session === session && pendingRefresh.version === version) {
    return pendingRefresh.promise;
  }
  const promise = getTokenFromClerkSession(true).then((refreshed) => {
    if (
      getAuthOperationVersion() !== version ||
      (session && clerkWindow.Clerk?.session !== session)
    ) {
      throw new Error("Session changed while refreshing authentication.");
    }
    if (refreshed) setToken(refreshed);
    return refreshed;
  });
  const entry = { session, version, promise };
  pendingRefresh = entry;
  const settled = () => {
    if (pendingRefresh === entry) pendingRefresh = null;
  };
  void promise.then(settled, settled);
  return promise;
};

const getAgentTokenFromClerkSession = async (forceRefresh: boolean): Promise<string> => {
  if (!clerkWindow.Clerk || !clerkWindow.Clerk.session) return "";
  const session = clerkWindow.Clerk.session;
  if (forceRefresh) {
    const token = await session.getToken({ template: AGENT_JWT_TEMPLATE, skipCache: true });
    return token ?? "";
  }
  const cached = await session.getToken({ template: AGENT_JWT_TEMPLATE, skipCache: false });
  if (!cached) return "";
  if (!isAgentTokenNearExpiry(cached)) return cached;
  const refreshed = await session.getToken({ template: AGENT_JWT_TEMPLATE, skipCache: true });
  return refreshed ?? cached;
};

const isAgentRequest = (url: string | undefined): boolean =>
  typeof url === "string" && url.startsWith(AGENT_RUNTIME_URL_PREFIX);

export const getToken: Resolver<string> = async (options) => {
  const shouldForceRefresh = Boolean(options.headers?.[FORCE_TOKEN_REFRESH_HEADER]);

  // Agent endpoint requires a Clerk JWT minted with the "nutricoach-agent" template
  // (custom aud claim). Clerk caches templated tokens internally, so we bypass the
  // localStorage cache used for the default session token.
  if (isAgentRequest(options.url)) {
    return getAgentTokenFromClerkSession(shouldForceRefresh);
  }

  const token = localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN_KEY);

  if (shouldForceRefresh) {
    return refreshSessionToken();
  }

  if (!token) {
    const sessionToken = await getTokenFromClerkSession(false);
    if (sessionToken) {
      setToken(sessionToken);
      return sessionToken;
    }
    breadcrumbNoTokenAvailable();
    return "";
  }

  const isNearExpiry = checkTokenExpiration(token);
  if (isNearExpiry) {
    const refreshedToken = await getTokenFromClerkSession(false);
    if (refreshedToken) {
      setToken(refreshedToken);
      return refreshedToken;
    }
    // A near-expiry token can still be valid, but never send an expired token
    // just because restoration returned empty. Clerk's auth guard owns routing
    // for ended sessions; temporary failures must not revoke a live session.
    if (isJwtExpired(`Bearer ${token}`)) {
      throw new Error("Unable to restore your session. Please try again.");
    }
    breadcrumbStaleTokenServed(token);
  }

  return token;
};

export const setToken = (token: string) => {
  localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN_KEY, token);
};

export const setIdHeader = (id: string) => {
  localStorage.setItem(LocalStorageKeys.ID_HEADER_KEY, id);
};

export const getIdHeader = () => {
  return localStorage.getItem(LocalStorageKeys.ID_HEADER_KEY) ?? "";
};

export const getHeaders: Resolver<Record<string, string>> = async (options: ApiRequestOptions) => {
  const API_KEY = env.VITE_API_KEY;
  const isDev = env.VITE_CLERK_ENV === "development";
  return {
    ...options.headers,
    "x-user-id": getIdHeader(),
    "x-api-key": API_KEY,
    "x-env-origin": isDev ? "dev" : "",
  };
};

let isSigningOut = false;
let hasRegisteredAuthInterceptor = false;

export const isJwtExpired = (authorizationHeader: unknown): boolean => {
  if (typeof authorizationHeader !== "string" || !authorizationHeader.startsWith("Bearer ")) {
    return true;
  }
  const token = authorizationHeader.slice("Bearer ".length).trim();
  if (!token) return true;
  const decoded = decodeJWT(token);
  const exp = decoded?.exp;
  if (typeof exp !== "number") return true;
  return exp * 1000 <= Date.now();
};

const isAgentRequestUrl = (url: unknown): boolean =>
  typeof url === "string" && url.includes(AGENT_RUNTIME_URL_PREFIX);

// AgentCore returns JSON-RPC errors on 401 with a `message` field that
// distinguishes recoverable token-lifecycle failures from misconfiguration
// ("Claim 'aud' value mismatch...") or missing-token bugs. Two messages are
// recoverable by minting a fresh templated token:
//   - "Token has expired": the JWT's `exp` has already passed.
//   - "Ineffectual token, will expire within the next minute": AgentCore
//     pre-emptively rejects any JWT whose remaining lifetime is < 60s.
export const AGENT_TOKEN_EXPIRED_MESSAGE = "Token has expired";
export const AGENT_INEFFECTUAL_TOKEN_MESSAGE =
  "Ineffectual token, will expire within the next minute";
export const AGENT_AUD_MISMATCH_MESSAGE = "Claim 'aud' value mismatch with configuration.";
export const AGENT_MISSING_TOKEN_MESSAGE = "Missing Authentication Token";
export const AGENT_INVALID_BEARER_MESSAGE = "Invalid Bearer token";

export const isRefreshableAgentTokenError = (data: unknown): boolean => {
  if (!data || typeof data !== "object") return false;
  const message = (data as { error?: { message?: unknown } }).error?.message;
  return message === AGENT_TOKEN_EXPIRED_MESSAGE || message === AGENT_INEFFECTUAL_TOKEN_MESSAGE;
};

const hasForceRefreshHeader = (headers: unknown): boolean => {
  if (!headers || typeof headers !== "object") return false;
  return Boolean((headers as Record<string, unknown>)[FORCE_TOKEN_REFRESH_HEADER]);
};

const signOutOnce = (context: { url?: string; authorizationHeader?: unknown } = {}) => {
  if (isSigningOut || !clerkWindow.Clerk) return;
  // A backend 401 proves the request failed, not that Clerk's session ended.
  // Only act on a healthy, initialized Clerk client with no current session.
  if (
    clerkWindow.Clerk.session ||
    !navigator.onLine ||
    (clerkWindow.Clerk.status ? clerkWindow.Clerk.status !== "ready" : !clerkWindow.Clerk.loaded)
  )
    return;
  isSigningOut = true;
  reportApi401SignOut(context);
  void clerkWindow.Clerk.signOut().catch((signOutError) => {
    isSigningOut = false;
    console.error("Failed to sign out after 401", signOutError);
  });
};

const registerAuthInterceptor = () => {
  if (hasRegisteredAuthInterceptor) return;
  hasRegisteredAuthInterceptor = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const status = error.response?.status;
      if (status !== 401) {
        return Promise.reject(error);
      }

      // Agent path: AgentCore tokens have a short TTL (600s); on expiry,
      // mint a fresh templated token and retry once. Do NOT sign out — the
      // Clerk session is still valid, only the cached agent token is stale.
      // Audience mismatches and missing-token bugs are surfaced to the caller
      // unchanged.
      if (isAgentRequestUrl(error.config?.url)) {
        if (!isRefreshableAgentTokenError(error.response?.data)) {
          return Promise.reject(error);
        }
        if (hasForceRefreshHeader(error.config?.headers)) {
          // Already retried with a force-refreshed token and still got expiry.
          // Surface the error rather than looping or signing the user out.
          return Promise.reject(error);
        }
        const retryConfig = {
          ...error.config,
          headers: {
            ...(error.config?.headers ?? {}),
            [FORCE_TOKEN_REFRESH_HEADER]: "true",
          },
        };
        return axios.request(retryConfig);
      }

      // Non-agent path: a 401 with an expired/absent sent-token can simply mean the
      // cached token went stale while Clerk was mid-rehydration (cold start / resume).
      // Force a fresh token from Clerk and retry once before signing out; only sign
      // when Clerk also confirms it has no session.
      const authorizationHeader = error.config?.headers?.["Authorization"];
      if (!isJwtExpired(authorizationHeader)) {
        return Promise.reject(error);
      }
      if (hasForceRefreshHeader(error.config?.headers)) {
        // Already retried with a freshly-minted token and still got a 401.
        signOutOnce({ url: error.config?.url, authorizationHeader });
        return Promise.reject(error);
      }
      const refreshedToken = await refreshSessionToken();
      if (!refreshedToken) {
        // Empty refresh alone is inconclusive; signOutOnce checks Clerk state.
        signOutOnce({ url: error.config?.url, authorizationHeader });
        return Promise.reject(error);
      }
      const retryConfig = {
        ...error.config,
        headers: {
          ...(error.config?.headers ?? {}),
          Authorization: `Bearer ${refreshedToken}`,
          [FORCE_TOKEN_REFRESH_HEADER]: "true",
        },
      };
      const response = await axios.request(retryConfig);
      breadcrumbToken401Recovered();
      return response;
    },
  );
};

export const initOpenApi = () => {
  OpenAPI.BASE = env.VITE_API_URL;
  OpenAPI.TOKEN = getToken;
  OpenAPI.HEADERS = getHeaders;
  registerAuthInterceptor();
};
